/**
 * Category Groups — client-side fallback + shape helpers.
 *
 * The source of truth for the 2-level catalog is the backend endpoint
 * `GET /api/categories/grouped/tree`. This module now serves two roles:
 *
 *  1) Deterministic fallback when the API is unavailable (offline, slow
 *     connections, or a stale build talking to an older backend that
 *     doesn't expose the endpoint yet). We reconstruct the 10 groups
 *     locally from the flat `/api/categories` response using the same
 *     mapping the backend uses.
 *  2) Backwards-compatible re-exports for older imports (LEAF_TO_GROUP,
 *     GROUP_BY_SLUG, buildGroups, pickPopular).
 *
 * Whenever the backend endpoint responds, the tree it returns wins — this
 * mapping is NEVER used to override server data.
 */

export const CATEGORY_GROUPS = [
  {
    slug: 'belleza',
    name_es: 'Belleza',
    name_en: 'Beauty',
    icon: 'Scissors',
    leaves: ['barberia', 'unas', 'belleza-estetica', 'tatuajes-piercings'],
  },
  {
    slug: 'bienestar',
    name_es: 'Bienestar',
    name_en: 'Wellness',
    icon: 'Flower',
    leaves: ['spa-masajes', 'yoga-meditacion'],
  },
  {
    slug: 'salud',
    name_es: 'Salud',
    name_en: 'Health',
    icon: 'Heart',
    leaves: ['odontologia', 'psicologia-terapia', 'nutricion', 'fisioterapia'],
    hiddenLeaves: ['salud'],
  },
  {
    slug: 'fitness',
    name_es: 'Fitness',
    name_en: 'Fitness',
    icon: 'Dumbbell',
    leaves: ['gimnasios'],
    hiddenLeaves: ['fitness-bienestar'],
  },
  {
    slug: 'hogar',
    name_es: 'Hogar',
    name_en: 'Home',
    icon: 'Home',
    leaves: ['servicios-hogar', 'reparaciones-hogar', 'limpieza-mudanzas', 'reparacion-electronica'],
  },
  {
    slug: 'automotriz',
    name_es: 'Automotriz',
    name_en: 'Automotive',
    icon: 'Car',
    leaves: ['automotriz'],
  },
  {
    slug: 'eventos',
    name_es: 'Eventos',
    name_en: 'Events',
    icon: 'PartyPopper',
    // `restaurantes-cafes` is intentionally hidden because that vertical
    // is dominated by OpenTable — not part of Bookvia's product scope.
    leaves: ['eventos-celebraciones', 'fotografia-video'],
    hiddenLeaves: ['restaurantes-cafes'],
  },
  {
    slug: 'consultoria',
    name_es: 'Consultoría',
    name_en: 'Consulting',
    icon: 'Briefcase',
    leaves: [
      'servicios-legales',
      'finanzas-contabilidad',
      'diseno-marketing',
      'coaching-personal',
      'bienes-raices',
    ],
    hiddenLeaves: ['consultoria'],
  },
  {
    slug: 'mascotas',
    name_es: 'Mascotas',
    name_en: 'Pets',
    icon: 'PawPrint',
    leaves: ['veterinaria'],
  },
  {
    slug: 'aprendizaje',
    name_es: 'Aprendizaje',
    name_en: 'Learning',
    icon: 'GraduationCap',
    leaves: ['clases-cursos', 'idiomas-traduccion', 'musica-arte'],
  },
];

export const LEAF_TO_GROUP = CATEGORY_GROUPS.reduce((acc, g) => {
  g.leaves.forEach((leaf) => { acc[leaf] = g.slug; });
  (g.hiddenLeaves || []).forEach((leaf) => { acc[leaf] = g.slug; });
  return acc;
}, {});

export const GROUP_BY_SLUG = CATEGORY_GROUPS.reduce((acc, g) => {
  acc[g.slug] = g;
  return acc;
}, {});

/** Build the 2-level tree locally from a flat `/api/categories` response. */
export function buildGroups(categories) {
  const bySlug = {};
  (categories || []).forEach((c) => {
    if (c?.slug) bySlug[c.slug] = c;
  });

  return CATEGORY_GROUPS.map((g) => {
    const leafItems = g.leaves.map((s) => bySlug[s]).filter(Boolean);
    const hiddenItems = (g.hiddenLeaves || [])
      .map((s) => bySlug[s])
      .filter(Boolean);

    const business_count =
      leafItems.reduce((sum, c) => sum + (c.business_count || 0), 0) +
      hiddenItems.reduce((sum, c) => sum + (c.business_count || 0), 0);

    const image_url = leafItems.find((c) => c.image_url)?.image_url || '';

    return { ...g, leafItems, business_count, image_url };
  });
}

/** Populares from a flat list, respecting hidden leaves. */
export function pickPopular(categories, limit = 6) {
  const hiddenSet = new Set();
  CATEGORY_GROUPS.forEach((g) => {
    (g.hiddenLeaves || []).forEach((s) => hiddenSet.add(s));
  });
  return [...(categories || [])]
    .filter((c) => !hiddenSet.has(c.slug))
    .sort((a, b) => {
      const diff = (b.business_count || 0) - (a.business_count || 0);
      if (diff !== 0) return diff;
      return (a.name_es || '').localeCompare(b.name_es || '');
    })
    .slice(0, limit);
}

/**
 * Group a flat categories array by `super_group`, preserving the display
 * order defined in `CATEGORY_GROUPS`. Useful for grouped `<Select>` /
 * `<optgroup>` renderings in the business registration and admin panels.
 *
 * Options:
 *  - includeHidden (default false): include categories flagged
 *    `hidden_from_picker=true`. When true, they are bucketed at the end
 *    under an "otras" group so admins can still reassign to them.
 *  - includeOther (default true): keep the catch-all "Otro" category
 *    (super_group null) inside a trailing "otros" bucket.
 */
export function groupCategoriesBySuperGroup(categories, options = {}) {
  const { includeHidden = false, includeOther = true } = options;
  const list = (categories || []).filter((c) => {
    if (!includeHidden && c.hidden_from_picker) return false;
    return true;
  });

  const buckets = new Map();
  CATEGORY_GROUPS.forEach((g) => {
    buckets.set(g.slug, {
      slug: g.slug,
      name_es: g.name_es,
      name_en: g.name_en,
      items: [],
    });
  });

  const trailing = { slug: 'otros', name_es: 'Otras', name_en: 'Others', items: [] };

  list.forEach((cat) => {
    // Prefer the server-provided super_group, but fall back to the local
    // leaf→group mapping when the backend is older/stale or omits the
    // field. Also normalize casing & whitespace so cosmetic drift in the
    // data (e.g. "Belleza" vs "belleza") doesn't drop the category into
    // the "otras" bucket.
    const raw = (cat.super_group || '').toString().trim().toLowerCase();
    const fallback = LEAF_TO_GROUP[cat.slug] || null;
    const key = raw && buckets.has(raw) ? raw : fallback;
    if (key && buckets.has(key)) {
      buckets.get(key).items.push(cat);
    } else if (includeOther) {
      trailing.items.push(cat);
    }
  });

  const groups = Array.from(buckets.values()).filter((g) => g.items.length > 0);
  if (trailing.items.length > 0) groups.push(trailing);

  // Alphabetize items inside each bucket for predictable UX.
  groups.forEach((g) => {
    g.items.sort((a, b) => (a.name_es || '').localeCompare(b.name_es || ''));
  });

  return groups;
}

/**
 * Normalize the shape of `/api/categories/grouped/tree` (server tree)
 * into the same object shape the `buildGroups()` helper produces, so
 * downstream UI code doesn't care about the source.
 */
export function normalizeServerTree(tree) {
  const groups = (tree?.groups || []).map((g) => ({
    slug: g.slug,
    name_es: g.name_es,
    name_en: g.name_en,
    icon: g.icon,
    display_order: g.display_order,
    image_url: g.image_url || '',
    business_count: g.business_count || 0,
    leaves: (g.categories || []).map((c) => c.slug),
    hiddenLeaves: [],
    leafItems: g.categories || [],
  }));
  const populares = tree?.populares || [];
  return { groups, populares };
}
